import { useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { CustomFormElement, Loader, FormSection, CustomButton } from '@/components/common';
import { ProfileWizardContext } from '@/modules/recruits/components';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { onboardingConstants } from '@/lib/constants';
import { updateWorkdayIdAsync } from '@/redux/reps';
import { workdayValidationSchema } from '../../lib';
import { workdayIdIsLoadingSelector } from '@/redux/loading';
import PropTypes from 'prop-types';

const {
  WORKER_ID,
  WORKER_ID_LABEL,
  WORKDAY_ID,
  WORKDAY_ID_LABEL,
  WORKDAY_HIRE_DATE,
  WORKDAY_HIRE_DATE_LABEL,
  WORKDAY_SECTION_NAME,
} = onboardingConstants;

const WorkdayID = ({
  userId,
  workerId,
  workdayId,
  workdayHireDate,
  updateWorkdayId,
  loading,
}) => {
  const { showViewHistory } = useContext(ProfileWizardContext);

  const methods = useForm({
    defaultValues: {
      [WORKER_ID]: workerId ?? '',
      [WORKDAY_ID]: workdayId ?? '',
      [WORKDAY_HIRE_DATE]: workdayHireDate ?? '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(workdayValidationSchema),
  });

  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    handleSubmit,
  } = methods;

  const onChangeHandler = useCallback(async (event) => {
    const { name, value } = event.target;

    setValue(name, value, { shouldValidate: true });

    // Any field change requires re-validation of the other fields.
    await trigger(
      [
        WORKER_ID,
        WORKDAY_ID,
        WORKDAY_HIRE_DATE,
      ].filter((field) => field !== name),
    );
  }, [setValue, trigger]);

  const handleSubmitForm = useCallback(() => {
    updateWorkdayId({
      userId,
      ...getValues(),
    });
  }, [
    getValues,
    updateWorkdayId,
    userId,
  ]);

  return (
    <div className={loading ? 'p-6' : undefined}>
      {loading
        ? <Loader />
        : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <FormSection
                viewHistory
                onViewHistory={() => {
                  showViewHistory(WORKDAY_SECTION_NAME);
                }}
              >
                <div className="grid grid-cols-1 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <CustomFormElement
                    id={WORKER_ID}
                    name={WORKER_ID}
                    label={WORKER_ID_LABEL}
                    type="text"
                    onChange={onChangeHandler}
                    register={register}
                    error={errors?.[WORKER_ID]}
                  />
                  <CustomFormElement
                    id={WORKDAY_ID}
                    name={WORKDAY_ID}
                    label={WORKDAY_ID_LABEL}
                    type="text"
                    onChange={onChangeHandler}
                    register={register}
                    error={errors?.[WORKDAY_ID]}
                  />
                  <CustomFormElement
                    id={WORKDAY_HIRE_DATE}
                    name={WORKDAY_HIRE_DATE}
                    label={WORKDAY_HIRE_DATE_LABEL}
                    type="date"
                    onChange={onChangeHandler}
                    register={register}
                    error={errors?.[WORKDAY_HIRE_DATE]}
                    maxDate={new Date()}
                  />
                </div>
                <div className="flex justify-end mt-4">
                  <CustomButton
                    type="submit"
                    color="green"
                  >
                    Save Changes
                  </CustomButton>
                </div>
              </FormSection>
            </form>
          </FormProvider>
        )}
    </div>
  );
};

WorkdayID.propTypes = {
  userId: PropTypes.number,
  workerId: PropTypes.string,
  workdayId: PropTypes.string,
  workdayHireDate: PropTypes.string,
  loading: PropTypes.bool,
  updateWorkdayId: PropTypes.func,
};

const mapStateToProps = (state) => ({
  loading: workdayIdIsLoadingSelector(state),
});

const mapDispatchToProps = {
  updateWorkdayId: updateWorkdayIdAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkdayID);
